import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { User, Mail, Phone, MessageSquare, X, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { useContactForm } from "@/context/ContactFormContext";
import { formContent } from "@/i18n/formContent";
import { API_BASE_URL } from "@/lib/apiBase";

const inputCls =
  "h-12 w-full rounded-2xl border border-transparent bg-mist/50 px-4 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-graphite/40 focus:border-sage focus:bg-white";

const ContactFormDrawer = () => {
  const { dir, lang } = useLanguage();
  const t = formContent[lang];
  const { open, closeForm } = useContactForm();
  const reduce = useReducedMotion();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeForm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeForm]);

  const slideX = dir === "rtl" ? "-100%" : "100%";

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
    const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      toast.success(t.success);
      setForm({ name: "", email: "", phone: "", message: "" });
      closeForm();
    } catch {
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="contact-backdrop"
            data-testid="contact-form-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeForm}
            className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm"
          />
          <motion.aside
            key="contact-panel"
            data-testid="contact-form-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={t.title}
            initial={reduce ? { opacity: 0 } : { x: slideX }}
            animate={reduce ? { opacity: 1 } : { x: 0 }}
            exit={reduce ? { opacity: 0 } : { x: slideX }}
            transition={reduce ? { duration: 0.2 } : { type: "spring", stiffness: 300, damping: 34 }}
            className="fixed inset-y-0 end-0 z-[71] flex w-full max-w-md flex-col bg-white shadow-soft"
          >
            <div className="flex items-start justify-between gap-4 p-6 pb-0 md:p-8 md:pb-0">
              <div>
                <p data-testid="contact-form-eyebrow" className="flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] text-graphite/50">
                  <span className="size-2 rounded-full bg-sage" />
                  {t.eyebrow}
                </p>
                <h2 className="font-display mt-3 text-2xl font-bold text-charcoal">{t.title}</h2>
                <p className="mt-1.5 text-sm leading-relaxed text-graphite/60">{t.sub}</p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                data-testid="contact-form-close"
                aria-label={t.close}
                className="flex size-9 shrink-0 items-center justify-center rounded-full border border-line bg-white text-graphite transition-colors duration-200 hover:border-sage hover:text-sage-deep"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={onSubmit} data-testid="contact-form" className="flex min-h-0 flex-1 flex-col">
              <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-6 md:p-8">
                <div>
                  <label htmlFor="cf-name" className="mb-2 flex items-center gap-2 text-sm font-semibold text-charcoal">
                    <User className="size-4 text-graphite/45" />
                    {t.name}
                    <span className="text-xs font-medium tabular-nums text-graphite/40" dir="ltr">[01]</span>
                  </label>
                  <input
                    id="cf-name"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={set("name")}
                    placeholder={t.namePh}
                    autoFocus
                    data-testid="contact-form-name-input"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label htmlFor="cf-email" className="mb-2 flex items-center gap-2 text-sm font-semibold text-charcoal">
                    <Mail className="size-4 text-graphite/45" />
                    {t.email}
                    <span className="text-xs font-medium tabular-nums text-graphite/40" dir="ltr">[02]</span>
                  </label>
                  <input
                    id="cf-email"
                    type="email"
                    required
                    maxLength={200}
                    value={form.email}
                    onChange={set("email")}
                    placeholder={t.emailPh}
                    dir="ltr"
                    data-testid="contact-form-email-input"
                    className={`${inputCls} text-start`}
                  />
                </div>

                <div>
                  <label htmlFor="cf-phone" className="mb-2 flex items-center gap-2 text-sm font-semibold text-charcoal">
                    <Phone className="size-4 text-graphite/45" />
                    {t.phone}
                    <span className="text-xs font-medium text-graphite/40">{t.optional}</span>
                    <span className="text-xs font-medium tabular-nums text-graphite/40" dir="ltr">[03]</span>
                  </label>
                  <input
                    id="cf-phone"
                    type="tel"
                    maxLength={40}
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder={t.phonePh}
                    dir="ltr"
                    data-testid="contact-form-phone-input"
                    className={`${inputCls} text-start`}
                  />
                </div>

                <div>
                  <label htmlFor="cf-message" className="mb-2 flex items-center gap-2 text-sm font-semibold text-charcoal">
                    <MessageSquare className="size-4 text-graphite/45" />
                    {t.message}
                    <span className="text-xs font-medium tabular-nums text-graphite/40" dir="ltr">[04]</span>
                  </label>
                  <textarea
                    id="cf-message"
                    required
                    minLength={50}
                    maxLength={2000}
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={t.messagePh}
                    data-testid="contact-form-message-input"
                    className="w-full resize-none rounded-2xl border border-transparent bg-mist/50 px-4 py-3.5 text-sm leading-relaxed text-ink outline-none transition-colors duration-200 placeholder:text-graphite/40 focus:border-sage focus:bg-white"
                  />
                </div>
              </div>

              <div className="border-t border-line p-6 md:px-8">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit"
                  className="flex h-[52px] w-full items-center justify-center gap-2.5 rounded-full bg-charcoal text-sm font-semibold text-softwhite transition-colors duration-200 hover:bg-graphite disabled:opacity-60"
                >
                  {sending ? t.sending : t.submit}
                  <Send className="size-4 rtl:-scale-x-100" />
                </button>
              </div>
            </form>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactFormDrawer;
